import React, { useMemo } from 'react'
import InputGroupContent from '../elements/input'
import { UseFormRegister, FieldErrors, UseFormSetValue } from 'react-hook-form'
import Select from '../elements/select'
import { CheckCircle, Mail, RefreshCw, MapPin, Shield } from 'lucide-react'
import municipiosData from '@/utils/muncipios.json'

interface RenderStepContentProps {
	currentStep: number
	isLoading: boolean
	skipAddress: boolean
	setSkipAddress: (value: boolean) => void
	resendEmail: () => void
	register: UseFormRegister<any>
	errors: FieldErrors<any>
	values: any
	setValue: UseFormSetValue<any>
}

const RenderStepContent: React.FC<RenderStepContentProps> = ({
	currentStep,
	isLoading,
	skipAddress,
	setSkipAddress,
	resendEmail,
	register,
	errors,
	values,
}: RenderStepContentProps) => {
	const watchProvince = values.address?.[0]?.province || ''

	const departmentOptions = useMemo(() => {
		return municipiosData.departamentos.map((dept) => ({
			label: dept.nombre,
			value: dept.nombre,
		}))
	}, [])

	const cityOptions = useMemo(() => {
		if (!watchProvince) return []

		const selectedDept = municipiosData.departamentos.find(
			(dept) => dept.nombre === watchProvince
		)

		if (!selectedDept) return []

		if (!selectedDept.municipios || selectedDept.municipios.length === 0) {
			return [
				{
					label: selectedDept.nombre,
					value: selectedDept.nombre,
				},
			]
		}

		return selectedDept.municipios.map((mun) => ({
			label: mun.nombre,
			value: mun.nombre,
		}))
	}, [watchProvince])

	const isDepartmentWithoutMunicipios = useMemo(() => {
		if (!watchProvince) return false

		const selectedDept = municipiosData.departamentos.find(
			(dept) => dept.nombre === watchProvince
		)

		return (
			selectedDept &&
			(!selectedDept.municipios || selectedDept.municipios.length === 0)
		)
	}, [watchProvince])

	switch (currentStep) {
		case 1:
			return (
				<div className='space-y-8 px-1'>
					<div className='space-y-2'>
						<InputGroupContent
							label='Correo electrónico'
							placeholder='tu.email@ejemplo.com'
							type='email'
							name='email'
							register={register}
							errors={errors}
							required={true}
						/>
					</div>

					<div className='bg-gray-50 rounded-xl p-5 border border-gray-200'>
						<div className='flex items-start space-x-4'>
							<div className='w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0'>
								<CheckCircle className='w-3.5 h-3.5 text-blue-600' />
							</div>
							<div className='flex-1 min-w-0'>
								<p className='text-sm font-semibold text-gray-800 leading-tight'>
									Te enviaremos un código de verificación
								</p>
								<p className='text-xs text-gray-600 mt-1.5 leading-relaxed'>
									Revisa tu bandeja de entrada y spam
								</p>
							</div>
						</div>
					</div>
				</div>
			)

		case 2:
			return (
				<div className='space-y-8 px-1'>
					<div className='bg-gradient-to-r from-red-50 to-red-100 rounded-xl p-6 border border-red-200'>
						<div className='flex items-center justify-between gap-4'>
							<div className='flex items-center space-x-4 flex-1 min-w-0'>
								<div className='w-11 h-11 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0'>
									<Mail className='w-5 h-5 text-white' />
								</div>
								<div className='flex-1 min-w-0'>
									<p className='text-sm font-bold text-red-900 leading-tight'>
										Código enviado a: {values.email}
									</p>
									<p className='text-xs text-red-700 mt-1 leading-relaxed'>
										¿No lo encuentras? Revisa spam
									</p>
								</div>
							</div>
							<button
								onClick={resendEmail}
								disabled={isLoading}
								className='bg-white hover:bg-gray-50 border-2 border-red-200 text-red-700 px-5 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 disabled:opacity-50 flex items-center space-x-2 flex-shrink-0'
							>
								{isLoading && <RefreshCw className='w-4 h-4 animate-spin' />}
								<span>{isLoading ? 'Enviando...' : 'Reenviar'}</span>
							</button>
						</div>
					</div>

					<div className='space-y-2'>
						<InputGroupContent
							label='Código de verificación'
							placeholder='123456'
							type='number'
							name='code'
							register={register}
							errors={errors}
							required={true}
						/>
					</div>
				</div>
			)

		case 3:
			return (
				<div className='space-y-8 px-1'>
					<div className='space-y-6'>
						<div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
							<InputGroupContent
								label='Nombre'
								placeholder='Tu nombre'
								type='text'
								name='name'
								register={register}
								errors={errors}
								required={true}
							/>
							<InputGroupContent
								label='Apellido'
								placeholder='Tu apellido'
								type='text'
								name='lastname'
								register={register}
								errors={errors}
								required={true}
							/>
						</div>

						<InputGroupContent
							label='Teléfono'
							placeholder='3001234567'
							type='number'
							name='phone'
							register={register}
							errors={errors}
							required={true}
						/>

						<div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
							<InputGroupContent
								label='Contraseña'
								type='password'
								placeholder='••••••••'
								name='password'
								register={register}
								errors={errors}
								required={true}
							/>
							<InputGroupContent
								label='Confirmar contraseña'
								type='password'
								placeholder='••••••••'
								name='confirmPassword'
								register={register}
								errors={errors}
								required={true}
							/>
						</div>
					</div>

					{values.password && (
						<div className='bg-gray-900 rounded-xl p-5 border-2 border-gray-800'>
							<div className='flex items-center space-x-3 mb-4'>
								<Shield className='w-5 h-5 text-white' />
								<h4 className='text-sm font-bold text-white'>
									Requisitos de seguridad:
								</h4>
							</div>
							<div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
								{[
									{
										check: values.password.length >= 8,
										text: '8+ caracteres',
									},
									{
										check: /[A-Z]/.test(values.password),
										text: 'Mayúscula',
									},
									{
										check: /[a-z]/.test(values.password),
										text: 'Minúscula',
									},
									{ check: /\d/.test(values.password), text: 'Número' },
								].map((req, idx) => (
									<div key={idx} className='flex items-center space-x-3'>
										<div
											className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors duration-200 ${
												req.check ? 'bg-green-500' : 'bg-gray-600'
											}`}
										>
											{req.check && (
												<CheckCircle className='w-2.5 h-2.5 text-white' />
											)}
										</div>
										<span
											className={`text-xs font-medium transition-colors duration-200 ${
												req.check ? 'text-green-400' : 'text-gray-400'
											}`}
										>
											{req.text}
										</span>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			)

		case 4:
			return (
				<div className='space-y-8 px-1'>
					<div className='bg-blue-50 rounded-xl p-5 border border-blue-200'>
						<div className='flex items-center justify-between gap-4'>
							<div className='flex items-start space-x-4 flex-1 min-w-0'>
								<div className='w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0'>
									<MapPin className='w-3.5 h-3.5 text-blue-600' />
								</div>
								<div className='flex-1 min-w-0'>
									<p className='text-sm font-semibold text-blue-800 leading-tight'>
										La dirección es opcional
									</p>
									<p className='text-xs text-blue-600 mt-1.5 leading-relaxed'>
										Puedes agregarla después desde tu perfil
									</p>
								</div>
							</div>
							<button
								onClick={() => setSkipAddress(!skipAddress)}
								className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 flex-shrink-0 ${
									skipAddress
										? 'bg-blue-600 text-white hover:bg-blue-700'
										: 'bg-white border-2 border-blue-200 text-blue-700 hover:bg-blue-50'
								}`}
							>
								{skipAddress ? 'Agregar dirección' : 'Omitir'}
							</button>
						</div>
					</div>

					{!skipAddress && (
						<div className='space-y-8'>
							<div className='space-y-6'>
								<div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
									<InputGroupContent
										label='Nombre completo'
										placeholder='Nombre para entregas'
										type='text'
										name='address.0.fullname'
										register={register}
										errors={errors}
										required={true}
									/>
									<Select
										name='address.0.document_type'
										label='Tipo de documento'
										placeholder='Selecciona el tipo de documento'
										options={[
											{ label: 'Cédula de Ciudadanía', value: 'CC' },
											{ label: 'Cédula de Extranjería', value: 'CE' },
											{ label: 'NIT', value: 'NIT' },
											{ label: 'Pasaporte', value: 'PP' },
										]}
										register={register}
										errors={errors}
										isRequired={true}
									/>
								</div>

								<InputGroupContent
									label='Número de documento'
									placeholder='12345678'
									type='number'
									name='address.0.document'
									register={register}
									errors={errors}
									required={true}
								/>
							</div>

							<div className='space-y-6'>
								<div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
									<Select
										name='address.0.province'
										label='Departamento'
										placeholder='Selecciona el departamento'
										options={departmentOptions}
										register={register}
										errors={errors}
										isRequired={true}
									/>
									<Select
										name='address.0.city'
										label='Ciudad'
										placeholder={
											watchProvince
												? 'Selecciona la ciudad'
												: 'Primero selecciona un departamento'
										}
										options={cityOptions}
										register={register}
										errors={errors}
										isRequired={true}
										isDisabled={!watchProvince || isDepartmentWithoutMunicipios}
									/>
								</div>

								<InputGroupContent
									label='Barrio'
									placeholder='Nombre del barrio'
									type='text'
									name='address.0.neighborhood'
									register={register}
									errors={errors}
									required={true}
								/>
							</div>

							<div className='space-y-6'>
								<div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
									<Select
										name='address.0.street_type'
										label='Tipo de vía'
										placeholder='Selecciona el tipo de vía'
										options={[
											{ label: 'Calle', value: 'Calle' },
											{ label: 'Carrera', value: 'Carrera' },
											{ label: 'Avenida', value: 'Avenida' },
											{ label: 'Diagonal', value: 'Diagonal' },
											{ label: 'Transversal', value: 'Transversal' },
										]}
										register={register}
										errors={errors}
										isRequired={true}
									/>
									<InputGroupContent
										label='Nombre de vía'
										placeholder='Ej: 15'
										type='text'
										name='address.0.street'
										register={register}
										errors={errors}
										required={true}
									/>
									<InputGroupContent
										label='Número'
										placeholder='Ej: 25-10'
										type='text'
										name='address.0.number_home'
										register={register}
										errors={errors}
										required={true}
									/>
								</div>

								<div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
									<Select
										name='address.0.type_home'
										label='Tipo de vivienda'
										placeholder='Selecciona el tipo de vivienda'
										options={[
											{ label: 'Casa', value: 'Casa' },
											{ label: 'Apartamento', value: 'Apartamento' },
											{ label: 'Oficina', value: 'Oficina' },
											{ label: 'Local Comercial', value: 'Local' },
										]}
										register={register}
										errors={errors}
										isRequired={true}
									/>
									<InputGroupContent
										label='Teléfono'
										placeholder='Teléfono de contacto'
										type='number'
										name='address.0.phone'
										register={register}
										errors={errors}
										required={true}
									/>
								</div>

								<InputGroupContent
									label='Código postal'
									placeholder='Ej: 660003'
									type='text'
									name='address.0.postal_code'
									register={register}
									errors={errors}
									required={true}
								/>

								<InputGroupContent
									label='Referencias adicionales (opcional)'
									placeholder='Casa blanca, portón negro, frente al parque...'
									type='text'
									name='address.0.additional_references'
									register={register}
									errors={errors}
									required={false}
								/>
							</div>
						</div>
					)}
				</div>
			)

		default:
			return null
	}
}

export default RenderStepContent
